import { supabase } from '@/lib/supabase';

/** A past conversation of the side chat (22/10). RLS keeps it to its owner. */
export interface ChatConversationListItem {
  id: string;
  title: string;
  updatedAt: string;
}

export const CHAT_HISTORY_PAGE_SIZE = 50;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Conversations with at least one message of the person (they have a title), newest first. */
export const listChatConversations = async (
  offset = 0
): Promise<{ items: ChatConversationListItem[]; hasMore: boolean }> => {
  const { data, error } = await supabase
    .from('dashboard_conversations')
    .select('id,title,updated_at')
    .eq('status', 'active')
    .not('title', 'is', null)
    .order('updated_at', { ascending: false })
    .range(offset, offset + CHAT_HISTORY_PAGE_SIZE);

  if (error) {
    throw new Error(error.message || 'Impossible de charger l’historique du chat.');
  }

  const rows = Array.isArray(data) ? data : [];
  const items = rows
    .slice(0, CHAT_HISTORY_PAGE_SIZE)
    .flatMap((row): ChatConversationListItem[] =>
      isRecord(row) &&
      typeof row.id === 'string' &&
      typeof row.title === 'string' &&
      typeof row.updated_at === 'string'
        ? [{ id: row.id, title: row.title, updatedAt: row.updated_at }]
        : []
    );
  return { items, hasMore: rows.length > CHAT_HISTORY_PAGE_SIZE };
};

/** An empty title gives the conversation back its title from the first message. */
export const renameChatConversation = async (
  conversationId: string,
  title: string
): Promise<string> => {
  const trimmed = title.trim().replace(/\s+/g, ' ').slice(0, 200);
  const { data, error } = await supabase
    .from('dashboard_conversations')
    .update({ title: trimmed || null })
    .eq('id', conversationId)
    .select('title')
    .single();

  if (error || !isRecord(data) || typeof data.title !== 'string') {
    throw new Error(error?.message || 'Impossible de renommer cette conversation.');
  }
  return data.title;
};

export const deleteChatConversation = async (conversationId: string): Promise<void> => {
  const { error } = await supabase
    .from('dashboard_conversations')
    .delete()
    .eq('id', conversationId);

  if (error) {
    throw new Error(error.message || 'Impossible de supprimer cette conversation.');
  }
};
